/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import style from './listItem.module.scss';
import { CategoryId, FileItem } from 'store/types';
import FormCheckbox from 'controls/checkbox/formCheckbox';
import Close from 'icons/close';
import { Skeleton } from '@mui/material';
import CircularLoader from 'components/loader/circular';
import { UseFileListIntrface } from 'hooks/files/useFileList';
import { ItemStatus, PreviewMap } from 'pages/upload/upload';
import CropperComponent, {
  CropperRef,
} from 'components/modal/croppModal/croppModal';
import CropperConnector from 'components/modal/croppModal/cropperConnector';
import { isImageFormat, isPermittedFormat, getMIMEType } from 'utils/utils';
import cx from 'classnames';
import ErrorIcon from 'icons/error';
import ContextActionsComponent from 'controls/contextActions/contextActions';
import CartineBackgroundIcon from 'icons/cartineBackground';
import isEmpty from 'lodash-es/isEmpty';
import CircleClose from 'icons/circles/circleClose';
import Circle from 'icons/circles/circle';
import Bubble from 'components/bubble/bubble';
import { setUploadModal } from './setUploadModal';
import { v4 as uuid } from 'uuid';

const MAX_SIZE = 5;
let worker = Worker ? new Worker('imagesWorker.js') : null;

export default function ListItem({
  item,
  onCheckboxChange,
  onDelete,
  previewList,
  previewMap,
  setPreviewMap,
  currentCategory,
  onStatusChange,
  globalChecked,
  emptyTagsFail,
}: {
  onCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  item: FileItem;
  onDelete: (fName: string, pName: string) => void;
  previewList: UseFileListIntrface;
  previewMap: PreviewMap;
  setPreviewMap: React.Dispatch<React.SetStateAction<PreviewMap>>;
  currentCategory: CategoryId;
  onStatusChange: React.Dispatch<
    React.SetStateAction<{
      [x: string]: ItemStatus;
    }>
  >;
  globalChecked: boolean;
  emptyTagsFail: boolean;
}) {
  const [currentModal, setCurrentModal] = useState<null | React.ReactElement>(
    null
  );
  const [isNew, setIsNew] = useState(true);
  const [lastGlobal] = useState(globalChecked);

  const cropRef = useRef<CropperRef>(null);
  const [cropperConnector] = useState<CropperConnector>(
    new CropperConnector(cropRef)
  );

  // Запустить процесс декодирования, а в это время рисовать загрузку
  const imageRef = useRef<HTMLImageElement>(null);
  const [isDecoded, setIsDecoded] = useState(false);

  // Данные текущей превью, может быть, а может и не быть
  const previewName = previewMap[item.id];
  const previewItem = previewList.list[previewName];

  // Если есть превью, рисовать будем ее
  const finalItem = previewItem || item;
  // Отображаться будет либо оригинальный src если он достаточно мал, либо сжатый
  const [finalSrc, setFinalSrc] = useState(finalItem.src);
  const [compressedSrc, setCompressedSrc] = useState('');

  // Важно чтобы при изменении finalItem finalSrc сбросился в finalItem.src, а compressedSrc в пустую строку
  useEffect(() => {
    setFinalSrc(finalItem.src);
    setCompressedSrc('');
  }, [finalItem.src]);

  const isImage = isImageFormat(getMIMEType(item.file.name));
  const isPermitted = isPermittedFormat(
    getMIMEType(item.file.name),
    currentCategory
  );

  const getStatus = () => {
    let result: ItemStatus = 'success';
    switch (true) {
      case !isPermitted:
        result = 'error';
        break;
      case !isImage && isEmpty(previewItem):
        result = 'warning';
        break;
    }

    return result;
  };

  const getStatusClass = () => {
    if (!isPermitted) {
      return style.error;
    }

    if (!isImage && !previewItem) {
      return style.warning;
    }
  };

  const currentStatus = getStatus();
  const isEditPreview = previewItem && currentStatus === 'success';

  useEffect(() => {
    if (globalChecked !== lastGlobal) {
      setIsNew(false);
    }
  }, [globalChecked]);

  useEffect(() => {
    onStatusChange((old) => {
      return { ...old, [item.id]: currentStatus };
    });
  }, [currentStatus]);

  useEffect(() => {
    const effect = async () => {
      setIsDecoded(false);

      const decode = async () => {
        // console.log('decode!');
        // console.log('imageRef.current', imageRef.current);
        // try {
        //     if (!imageRef.current) {
        //     }
        //     imageRef.current?.onload = () => {
        //         setIsDecoded(true);
        //     };
        // } catch (e) {
        //   console.error(e);
        // }

        // imageRef.current
        try {
          await imageRef?.current?.decode();
        } catch (e) {
          console.error(e);
        }
        setIsDecoded(true);
      };

      const applyCompressedBitmap = (imageBitmapCompressed: ImageBitmap) => {
        const canvas = document.createElement('canvas');
        canvas.width = imageBitmapCompressed.width;
        canvas.height = imageBitmapCompressed.height;

        let ctx = canvas.getContext('bitmaprenderer');
        if (ctx) {
          ctx.transferFromImageBitmap(imageBitmapCompressed);
          canvas.toBlob(async (blob) => {
            if (blob) {
              const objUrl = URL.createObjectURL(blob);
              if (imageRef.current) {
                imageRef.current.src = objUrl;
                setCompressedSrc(objUrl);
              }
            }
          });
        }
      };

      // Если ранее появился compressedSrc, то не нужно тратиться на генерацию
      // bitmap для проверки размера, сразу декодируем
      if (imageRef.current && compressedSrc) {
        decode();
      } else if (imageRef.current && finalSrc) {
        // Если воркер поддерживается - сжимать будет он
        if (worker) {
          // При тесте на 31 файле общим весом 160 МБ удалось сократить время примерно на 25-30% благодаря воркеру
          // (Создание воркеров на каждую картинку дает меньший выигрышь примерно 15%)
          const listenerId = uuid();
          const workerListener = async (e: MessageEvent<any>) => {
            if (
              e.data.cmd === 'compress_if_requires' &&
              e.data.listenerId === listenerId
            ) {
              worker?.removeEventListener('message', workerListener);
              const imageBitmapCompressed = e.data.result.imageBitmapCompressed;

              if (imageBitmapCompressed) {
                applyCompressedBitmap(imageBitmapCompressed);
              } else {
                decode();
              }
            }
          };

          worker.addEventListener('message', workerListener);
          worker.postMessage({
            cmd: 'compress_if_requires',
            listenerId,
            data: {
              objectURL: finalSrc,
            },
          });
        } else {
          // Код на случай если не поддерживаются воркеры
          const srcBlob = await fetch(finalSrc).then((r) => r.blob());
          const imageBitmap = await createImageBitmap(srcBlob);
          const size = (imageBitmap.width * imageBitmap.height) / 1024 / 1024;

          if (size > MAX_SIZE) {
            const coeff = size / MAX_SIZE;
            const compressedW = Math.round(imageBitmap.width / coeff);
            const compressedH = Math.round(imageBitmap.height / coeff);
            const imageBitmapCompressed = await createImageBitmap(imageBitmap, {
              resizeWidth: compressedW,
              resizeHeight: compressedH,
            });
            applyCompressedBitmap(imageBitmapCompressed);
          } else {
            decode();
          }
        }
      }
    };

    effect();
  }, [finalSrc, compressedSrc]);

  const setUploadModalParams = {
    originalId: item.id,
    setCurrentModal,
    setPreviewMap,
  };

  const getBody = () => {
    if (currentStatus === 'error') {
      return (
        <div className={style.statusInfo}>
          <div className={style.texts}>
            <span className={cx(style.text, style.textMain)}>Ошибка</span>
            <span className={style.text}>Недопустимый формат</span>
          </div>
        </div>
      );
    }

    if (currentStatus === 'warning') {
      return (
        <div className={style.statusInfo}>
          <CartineBackgroundIcon></CartineBackgroundIcon>
          <div className={style.texts}>
            <span className={cx(style.text, style.textMain)}>
              Контент загружен
            </span>
            <span
              className={cx(style.text, style.textUnder)}
              onClick={() => {
                setUploadModal({ ...setUploadModalParams, previewList });
              }}
            >
              Добавьте миниатюру
            </span>
            <span className={style.text}>в формате JPG или PNG</span>
          </div>
        </div>
      );
    }

    return (
      <div className={style.imageWrapper}>
        {!isDecoded ? (
          <div>
            <Skeleton variant="rectangular" width={'100%'} height={'100%'} />
            <CircularLoader className={style.loader} />
          </div>
        ) : (
          ''
        )}
        <img
          className={!isDecoded ? 'hidden' : style.image}
          src={finalSrc}
          alt={finalItem.originalName}
        //   loading="lazy"
          draggable="false"
          ref={imageRef}
        />
      </div>
    );
  };

  return (
    <div className={cx(style.container, getStatusClass(), 'noselect')}>
      {currentModal !== null ? currentModal : ''}
      <CropperComponent
        ref={cropRef}
        cropperConnector={cropperConnector}
        replaceFile={(oldF, newF) => {
          if (oldF && newF) {
            const dt = new DataTransfer();
            dt.items.add(new File([newF], oldF.name, { type: newF.type }));
            var fileList = dt.files;

            previewList.removeFile(oldF.name);

            previewList.addFiles(fileList);
          }
        }}
      ></CropperComponent>

      {getBody()}

      {currentStatus === 'success' ? (
        <div>
          <div className={style.checkboxBackground}></div>
          <FormCheckbox
            name={item.id as `${string}`}
            inner_props={{
              form_control_label_props: {
                label: '',
              },
              checkbox_props: {
                onChange: (e: any) => {
                  onCheckboxChange(e);

                  if (e.isTrusted) {
                    setIsNew(false);
                  }
                },
                checked: globalChecked || isNew,
              },
            }}
          ></FormCheckbox>
        </div>
      ) : (
        ''
      )}

      <div className={style.icons}>
        {/* Иконка добавьте теги */}
        {currentStatus === 'success' && emptyTagsFail && isEmpty(item.tags) ? (
          <div className={cx(style.emptyTags, 'noselect')}>добавьте теги</div>
        ) : (
          ''
        )}

        {/* Иконка с восклицательным знаком */}
        {currentStatus === 'warning' || currentStatus === 'error' ? (
          <ErrorIcon></ErrorIcon>
        ) : (
          ''
        )}

        {/* Иконка редактирования превью */}
        {isEditPreview ? (
          <div className={style.circle}>
            <Circle>
              <ContextActionsComponent
                actions={[
                  {
                    type: 'edit',
                    handler: () => {
                      cropperConnector.open(previewItem.file);
                    },
                  },
                  {
                    type: 'replace',
                    handler: setUploadModal.bind(null, {
                      ...setUploadModalParams,
                      previewList,
                    }),
                  },
                ]}
                iconType="cartine"
                iconClassName={style.iconCartine}
                bubbleClassName={style.bubble}
              ></ContextActionsComponent>
            </Circle>
          </div>
        ) : (
          ''
        )}

        {/* Иконка крестика */}
        <CircleClose onClick={() => onDelete(item.id, previewName)}>
          <Close></Close>
        </CircleClose>
      </div>

      <div className={style.fileNameWrapper}>
        <div
          className={cx(
            style.fileName,
            currentStatus === 'success' ? style.gradient : ''
          )}
        >
          <span>{item.originalName}</span>
        </div>
        <Bubble className={cx(style.bubble)}>
          <span>{item.originalName}</span>
        </Bubble>
      </div>
    </div>
  );
}
